
import React from 'react';
import { DeepAnalysisResult } from '@/types/analysisTypes';
import { getAudienceScore, getAudienceExplanation, getAudienceImprovement } from './utils';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ShareabilityDisplayProps {
  pageAnalysisResult: DeepAnalysisResult;
}

export const ShareabilityDisplay: React.FC<ShareabilityDisplayProps> = ({ pageAnalysisResult }) => {
  const [activeAudience, setActiveAudience] = React.useState('beginner');
  const [expandedCards, setExpandedCards] = React.useState<Record<string, boolean>>({});

  if (!pageAnalysisResult || !pageAnalysisResult.shareability_scores) {
    return (
      <Alert variant="destructive" className="my-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Shareability data is missing or unavailable
        </AlertDescription>
      </Alert>
    );
  }

  const { shareability_scores } = pageAnalysisResult;
  
  const beginnerScore = getAudienceScore(shareability_scores.beginner);
  const intermediateScore = getAudienceScore(shareability_scores.intermediate);
  const expertScore = getAudienceScore(shareability_scores.expert);

  const toggleCardExpansion = (audienceType: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [audienceType]: !prev[audienceType]
    }));
  };

  const renderExpandableText = (text: string, audienceType: string, maxLength: number = 100) => {
    const isExpanded = expandedCards[audienceType];
    const shouldTruncate = text.length > maxLength;
    
    if (!shouldTruncate) {
      return <span>{text}</span>;
    }
    
    return (
      <div className="space-y-2">
        <span>
          {isExpanded ? text : `${text.slice(0, maxLength)}...`}
        </span>
        <button
          onClick={() => toggleCardExpansion(audienceType)}
          className="block text-xs text-primary hover:text-primary/80 font-medium transition-colors"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Audience Shareability Analysis</h3>
        <p className="text-muted-foreground mb-6">
          These scores indicate how likely different audience segments are to engage with and share your content.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className={`${activeAudience === 'beginner' ? 'border-primary' : 'border'} hover:border-primary transition-all cursor-pointer`} 
                onClick={() => setActiveAudience('beginner')}>
            <CardContent className="p-6">
              <div className="mb-4">
                <h4 className="font-medium">Beginner Audience</h4>
                <p className="text-sm text-muted-foreground">New to the topic</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Shareability Score</span>
                  <span className="text-sm font-bold">{beginnerScore}/100</span>
                </div>
                <Progress value={beginnerScore} className="h-2" />
              </div>
              {getAudienceExplanation(shareability_scores.beginner) && (
                <div className="mt-4 text-sm text-muted-foreground">
                  {renderExpandableText(getAudienceExplanation(shareability_scores.beginner), 'beginner-card')}
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card className={`${activeAudience === 'intermediate' ? 'border-primary' : 'border'} hover:border-primary transition-all cursor-pointer`}
                onClick={() => setActiveAudience('intermediate')}>
            <CardContent className="p-6">
              <div className="mb-4">
                <h4 className="font-medium">Intermediate Audience</h4>
                <p className="text-sm text-muted-foreground">Has basic knowledge</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Shareability Score</span>
                  <span className="text-sm font-bold">{intermediateScore}/100</span>
                </div>
                <Progress value={intermediateScore} className="h-2" />
              </div>
              {getAudienceExplanation(shareability_scores.intermediate) && (
                <div className="mt-4 text-sm text-muted-foreground">
                  {renderExpandableText(getAudienceExplanation(shareability_scores.intermediate), 'intermediate-card')}
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card className={`${activeAudience === 'expert' ? 'border-primary' : 'border'} hover:border-primary transition-all cursor-pointer`}
                onClick={() => setActiveAudience('expert')}>
            <CardContent className="p-6">
              <div className="mb-4">
                <h4 className="font-medium">Expert Audience</h4>
                <p className="text-sm text-muted-foreground">Domain specialist</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Shareability Score</span>
                  <span className="text-sm font-bold">{expertScore}/100</span>
                </div>
                <Progress value={expertScore} className="h-2" />
              </div>
              {getAudienceExplanation(shareability_scores.expert) && (
                <div className="mt-4 text-sm text-muted-foreground">
                  {renderExpandableText(getAudienceExplanation(shareability_scores.expert), 'expert-card')}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Tabs value={activeAudience} onValueChange={setActiveAudience}>
        <TabsList className="grid grid-cols-3">
          <TabsTrigger value="beginner">Beginner</TabsTrigger>
          <TabsTrigger value="intermediate">Intermediate</TabsTrigger>
          <TabsTrigger value="expert">Expert</TabsTrigger>
        </TabsList>
        <TabsContent value="beginner" className="p-4 border rounded-md mt-4">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium">Beginner Audience Insights</h4>
              <Progress value={beginnerScore} className="h-2 mt-2" />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-muted-foreground">Poor</span>
                <span className="text-xs text-muted-foreground">Excellent</span>
              </div>
            </div>
            
            {getAudienceExplanation(shareability_scores.beginner) && (
              <div>
                <h5 className="text-sm font-medium">Analysis</h5>
                <p className="text-sm mt-1">
                  {getAudienceExplanation(shareability_scores.beginner)}
                </p>
              </div>
            )}
            
            {getAudienceImprovement(shareability_scores.beginner) && (
              <div>
                <h5 className="text-sm font-medium">How to Improve</h5>
                <p className="text-sm mt-1">
                  {getAudienceImprovement(shareability_scores.beginner)}
                </p>
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="intermediate" className="p-4 border rounded-md mt-4">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium">Intermediate Audience Insights</h4>
              <Progress value={intermediateScore} className="h-2 mt-2" />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-muted-foreground">Poor</span>
                <span className="text-xs text-muted-foreground">Excellent</span>
              </div>
            </div>
            
            {getAudienceExplanation(shareability_scores.intermediate) && (
              <div>
                <h5 className="text-sm font-medium">Analysis</h5>
                <p className="text-sm mt-1">
                  {getAudienceExplanation(shareability_scores.intermediate)}
                </p>
              </div>
            )}
            
            {getAudienceImprovement(shareability_scores.intermediate) && (
              <div>
                <h5 className="text-sm font-medium">How to Improve</h5>
                <p className="text-sm mt-1">
                  {getAudienceImprovement(shareability_scores.intermediate)}
                </p>
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="expert" className="p-4 border rounded-md mt-4">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium">Expert Audience Insights</h4>
              <Progress value={expertScore} className="h-2 mt-2" />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-muted-foreground">Poor</span>
                <span className="text-xs text-muted-foreground">Excellent</span>
              </div>
            </div>
            
            {getAudienceExplanation(shareability_scores.expert) && (
              <div>
                <h5 className="text-sm font-medium">Analysis</h5>
                <p className="text-sm mt-1">
                  {getAudienceExplanation(shareability_scores.expert)}
                </p>
              </div>
            )}
            
            {getAudienceImprovement(shareability_scores.expert) && (
              <div>
                <h5 className="text-sm font-medium">How to Improve</h5>
                <p className="text-sm mt-1">
                  {getAudienceImprovement(shareability_scores.expert)}
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
